<template>
  <ValidationObserver
    ref="validationObserver"
    v-slot="{ invalid }"
    slim
  >
    <ModalStructure
      :class="$style.modal"
      hasBlur
      :showModal="true"
      showCloseButton
      @close="handleClose"
    >
      <template v-slot:header>
        <HeadingLarge>
          Submit An Idea
        </HeadingLarge>
      </template>
      <template v-slot:body>
        <FeedbackForm
          v-model="value"
          @input="updateValue"
        />
      </template>
      <template v-slot:footer>
        <ButtonGroupSave
          ref="actions"
          :class="$style.buttonGroup"
          :isDisabledPrimary="invalid"
          :isFullWidth="false"
          :isLoadingPrimary="loading"
          :primaryButtonProps="primaryButtonProps"
          @primaryAction="handleSubmit"
          @secondaryAction="handleClose"
        />
      </template>
    </ModalStructure>
  </ValidationObserver>
</template>

<script>
import {
  ButtonGroupSave,
  HeadingLarge,
  ModalStructure,
  Types,
} from '@jumpcloud/ui-components';
import { ValidationObserver } from 'vee-validate3';
import { mapActions } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FeedbackApi from 'api/v2/FeedbackApi';
import FeedbackForm from 'client/Support/FeedbackForm.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';

const getDefaultFeedbackData = () => ({
  category: {},
  description: '',
  details: '',
  priority: 'nice-to-have',
});

const { PrimaryButtonProps } = Types;

export default {
  name: 'SubmitIdeaModal',

  EventHelper,
  FeedbackApi,
  ModalEventBus,

  components: {
    ButtonGroupSave,
    HeadingLarge,
    ModalStructure,
    FeedbackForm,
    ValidationObserver,
  },

  props: {
    submissionFilter: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      enableMainActionFlag: false,
      loading: false,
      value: getDefaultFeedbackData(),
    };
  },

  computed: {
    formattedIdea() {
      const formattedSubmissionFilter = this.submissionFilter ? `${this.submissionFilter}\n` : '';
      return `${formattedSubmissionFilter}*What would make your experience with JumpCloud better?*\n${this.value.description}\n*What problem are you trying to solve? Have you seen this problem solved elsewhere?*\n${this.value.details}`;
    },
    primaryButtonProps() {
      return PrimaryButtonProps({
        text: 'Submit Idea',
      });
    },
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    handleClose() {
      ModalEventBus.close();
    },

    handleSubmit() {
      this.loading = true;

      FeedbackApi.postFeedback({
        category: this.value.category.name,
        idea: this.formattedIdea,
        priority: this.value.priority,
        productServiceGroup: this.value.category.productServiceGroup,
        sfdcSubject: this.value.description,
      })
        .then(() => {
          this.$options.EventHelper.triggerSubmitIdea({ submissionFilter: this.submissionFilter });
          this.value = getDefaultFeedbackData();

          this.triggerSuccessNotification({
            message: '**Success! Your idea has been submitted.**',
            isMarkdown: true,
            showCloseButton: true,
          });
          this.$router.push({ name: 'CasePortal' });

          this.handleClose();
        })
        .catch(() => {
          this.triggerErrorNotification({
            icon: '',
            emoji: '🚫',
            title: 'Error',
            message: 'We are unable to submit your idea at the moment. Please try again!',
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateValue(value) {
      this.value = value;
    },
  },
};
</script>

<style module scoped>
.modal {
  --modal-width: 48rem;
}

.buttonGroup {
  flex: 0 0 100%;
}
</style>
